import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import type { ApiResponse, AccountCategory } from '../types';

export type AccountSummary = {
  accountId: string;
  code: string;
  name: string;
  category: AccountCategory;
  ntaCategoryCode: string | null;
  debitTotal: number;
  creditTotal: number;
  balance: number;
};

export type TrialBalanceData = {
  summaries: AccountSummary[];
  totalsByCategory: Record<AccountCategory, number>;
  grandTotal: { debit: number; credit: number; balanced: boolean };
};

export type IncomeStatementData = {
  revenues: AccountSummary[];
  expenses: AccountSummary[];
  totalRevenue: number;
  totalExpense: number;
  netIncome: number;
};

export type BalanceSheetData = {
  asOf: string;
  assets: AccountSummary[];
  liabilities: AccountSummary[];
  equities: AccountSummary[];
  netIncome: number;
  totalAssets: number;
  totalLiabilities: number;
  totalEquity: number;
  balanced: boolean;
};

export type ConsumptionTaxData = {
  fiscalYear: number;
  dateFrom: string;
  dateTo: string;
  sales: { net10: number; net8: number; outputTax10: number; outputTax8: number; outputTaxTotal: number };
  purchases: {
    net10WithInvoice: number;
    net8WithInvoice: number;
    net10WithoutInvoice: number;
    net8WithoutInvoice: number;
    inputTax10: number;
    inputTax8: number;
    inputTaxTotal: number;
    uncreditableNote: string;
  };
  taxPayable: number;
};

export type JournalReportRow = {
  entryId: string;
  entryDate: string;
  description: string;
  taxType: string;
  isTaxInclusive: number;
  status: string;
  lineId: string;
  lineType: 'debit' | 'credit';
  accountCode: string;
  accountName: string;
  amount: number;
};

export type GeneralLedgerRow = {
  entryDate: string;
  entryId: string;
  description: string;
  debitAmount: number;
  creditAmount: number;
  balance: number;
  status: string;
};

export type GeneralLedgerData = {
  account: { id: string; code: string; name: string; category: string };
  lines: GeneralLedgerRow[];
  finalBalance: number;
};

type Filters = {
  dateFrom?: string;
  dateTo?: string;
  fiscalYear?: number;
};

function buildQuery(extra: Record<string, string | number | undefined>): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(extra)) {
    if (v !== undefined && v !== '') params.set(k, String(v));
  }
  const q = params.toString();
  return q ? `?${q}` : '';
}

export function useJournalReport(filters: Filters) {
  return useQuery({
    queryKey: ['report-journal', filters],
    queryFn: async () => {
      const res = await api.get<ApiResponse<JournalReportRow[]>>(
        `/api/reports/journal${buildQuery(filters)}`,
      );
      return res.data;
    },
  });
}

export function useGeneralLedger(accountId: string | undefined, filters: Filters) {
  return useQuery({
    queryKey: ['report-general-ledger', accountId, filters],
    enabled: !!accountId,
    queryFn: async () => {
      const res = await api.get<ApiResponse<GeneralLedgerData>>(
        `/api/reports/general-ledger${buildQuery({ accountId, ...filters })}`,
      );
      return res.data;
    },
  });
}

export function useTrialBalance(filters: Filters) {
  return useQuery({
    queryKey: ['report-trial-balance', filters],
    queryFn: async () => {
      const res = await api.get<ApiResponse<TrialBalanceData>>(
        `/api/reports/trial-balance${buildQuery(filters)}`,
      );
      return res.data;
    },
  });
}

export function useIncomeStatement(filters: Filters) {
  return useQuery({
    queryKey: ['report-income-statement', filters],
    queryFn: async () => {
      const res = await api.get<ApiResponse<IncomeStatementData>>(
        `/api/reports/income-statement${buildQuery(filters)}`,
      );
      return res.data;
    },
  });
}

export function useBalanceSheet(fiscalYear: number | undefined, asOf?: string) {
  return useQuery({
    queryKey: ['report-balance-sheet', fiscalYear, asOf],
    enabled: fiscalYear !== undefined,
    queryFn: async () => {
      const res = await api.get<ApiResponse<BalanceSheetData>>(
        `/api/reports/balance-sheet${buildQuery({ fiscalYear, asOf })}`,
      );
      return res.data;
    },
  });
}

export function useConsumptionTax(fiscalYear: number | undefined) {
  return useQuery({
    queryKey: ['report-consumption-tax', fiscalYear],
    enabled: fiscalYear !== undefined,
    queryFn: async () => {
      const res = await api.get<ApiResponse<ConsumptionTaxData>>(
        `/api/reports/consumption-tax${buildQuery({ fiscalYear })}`,
      );
      return res.data;
    },
  });
}

export function downloadCsv(path: string, filters: Record<string, string | number | undefined>) {
  const url = `${path}${buildQuery({ ...filters, format: 'csv' })}`;
  // 認証クッキーを付けて取得（fetch経由でblobダウンロード）
  void (async () => {
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) {
      alert(`CSV取得失敗: ${res.status}`);
      return;
    }
    const blob = await res.blob();
    const link = document.createElement('a');
    const objectUrl = URL.createObjectURL(blob);
    link.href = objectUrl;
    const filename = res.headers
      .get('Content-Disposition')
      ?.match(/filename\*=UTF-8''([^;]+)/)?.[1];
    link.download = filename ? decodeURIComponent(filename) : 'export.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  })();
}
